import React, { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getAllPayments } from '../../api/getAllPayments';

const RoleAccessLayer = () => {
  const [payments, setPayments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [initialLoading, setInitialLoading] = useState(true); // Spinner state
  const itemsPerPage = 10;

  useEffect(() => {
    const fetchPayments = async () => {
      const data = await getAllPayments();
      setPayments(data);
      setInitialLoading(false); // hide loader
    };
    fetchPayments();
  }, []);

  const filteredData = payments.filter((item) => {
    const fullText = `${item.teachername} ${item.studentname} ${item.subjectname} ${item.payment_status}`.toLowerCase();
    const matchesSearch = fullText.includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === '' || item.payment_status.toLowerCase() === statusFilter.toLowerCase();
    const itemDate = new Date(item.bookdate);
    const fromDateMatch = startDate ? itemDate >= new Date(startDate) : true;
    const toDateMatch = endDate ? itemDate <= new Date(endDate) : true;
    return matchesSearch && matchesStatus && fromDateMatch && toDateMatch;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const exportToExcel = () => {
    const heading = [["Payment List"]];
    const data = filteredData.map((item, i) => ({
      "S.L": i + 1,
      "Book Date": item.bookdate,
      "Teacher": item.teachername,
      "Student": item.studentname,
      "Subject": item.subjectname,
      "Start": item.slot_start,
      "End": item.slot_end,
      "Amount": item.amount,
      "Status": item.payment_status,
    }));
    const worksheet = XLSX.utils.json_to_sheet(data, { origin: -1 });
    XLSX.utils.sheet_add_aoa(worksheet, heading, { origin: "A1" });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Payments");
    XLSX.writeFile(workbook, "payments.xlsx");
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text("Payment List", 14, 20);
    const columns = ["S.L", "Book Date", "Teacher", "Student", "Subject", "Start", "End", "Amount", "Status"];
    const rows = filteredData.map((item, i) => ([
      i + 1,
      item.bookdate,
      item.teachername,
      item.studentname,
      item.subjectname,
      item.slot_start,
      item.slot_end,
      item.amount,
      item.payment_status,
    ]));
    autoTable(doc, {
      startY: 25,
      head: [columns],
      body: rows,
    });
    doc.save("payments.pdf");
  };

  if (initialLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
        <div style={{
          width: '48px',
          height: '48px',
          border: '6px solid #e0e0e0',
          borderTop: '6px solid #45B369',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="card h-100 p-0 radius-12">
      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
        <div className="d-flex align-items-center flex-wrap gap-3">
          <input
            type="text"
            className="form-control w-auto"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <input
            type="date"
            className="form-control w-auto"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <input
            type="date"
            className="form-control w-auto"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
          <select
            className="form-select form-select-sm w-auto"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All</option>
            <option value="Paid">Paid</option>
            <option value="Free">Free</option>
          </select>
          <button
            onClick={() => {
              setSearchTerm('');
              setStatusFilter('');
              setStartDate('');
              setEndDate('');
            }}
            className="btn btn-outline-secondary btn-sm"
          >
            Reset Filters
          </button>
          <button onClick={exportToExcel} className="btn btn-success btn-sm">Excel Export</button>
          <button onClick={exportToPDF} className="btn btn-danger btn-sm">PDF Export</button>
        </div>
      </div>

      <div className="card-body p-24">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table sm-table mb-0">
            <thead>
              <tr>
                <th>S.L</th>
                <th>Book Date</th>
                <th>Teacher</th>
                <th>Student</th>
                <th>Subject</th>
                <th>Slot Start</th>
                <th>Slot End</th>
                <th>Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.map((item, index) => (
                <tr key={index}>
                  <td>{indexOfFirstItem + index + 1}</td>
                  <td>{item.bookdate}</td>
                  <td>{item.teachername}</td>
                  <td>{item.studentname}</td>
                  <td>{item.subjectname}</td>
                  <td>{item.slot_start}</td>
                  <td>{item.slot_end}</td>
                  <td>${item.amount}</td>
                  <td>
                    <span className={`badge ${item.payment_status === 'Paid' ? 'bg-success' : 'bg-warning'}`}>
                      {item.payment_status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="d-flex justify-content-between mt-3">
          <span>
            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredData.length)} of {filteredData.length} entries
          </span>
          <ul className="pagination">
            {[...Array(totalPages)].map((_, i) => (
              <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                <button onClick={() => setCurrentPage(i + 1)} className="page-link">
                  {i + 1}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default RoleAccessLayer;
